import { useState, type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { Check } from 'lucide-react'
import { toast } from 'sonner'
import { cancelButtonLabel } from '../lib/commonStrings'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'

interface Props<C extends string> {
  open: boolean
  onOpenChange: (open: boolean) => void
  description: ReactNode
  categories: readonly C[]
  categoryLabel: (category: C) => string
  /** Categories the viewer already reported and are still pending — shown, not selectable. */
  pendingCategories: ReadonlySet<C>
  /** Categories that say nothing without a note. */
  noteRequired: (category: C) => boolean
  notePlaceholder: (category: C | null) => string
  /** Adds an optional "where did you see it?" link field. */
  withLink?: boolean
  onSubmit: (category: C, message: string, url: string) => Promise<void>
  successToast: string
}

/**
 * The "What's wrong?" category picker behind every structured report — one
 * category, an optional note (required where the category needs one), and
 * optionally a link. The caller owns where the report goes.
 */
export function ProblemReportDialog<C extends string>({
  open,
  onOpenChange,
  description,
  categories,
  categoryLabel,
  pendingCategories,
  noteRequired,
  notePlaceholder,
  withLink,
  onSubmit,
  successToast,
}: Props<C>) {
  const [category, setCategory] = useState<C | null>(null)
  const [message, setMessage] = useState('')
  const [url, setUrl] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const linkValid = !url.trim() || /^https?:\/\/\S+$/.test(url.trim())
  const canSubmit =
    !!category &&
    !pendingCategories.has(category) &&
    (!noteRequired(category) || message.trim().length > 0) &&
    linkValid

  function handleOpenChange(next: boolean) {
    onOpenChange(next)
    if (!next) {
      setCategory(null)
      setMessage('')
      setUrl('')
    }
  }

  async function submit() {
    if (!category) return
    setSubmitting(true)
    try {
      await onSubmit(category, message, url.trim())
      toast.success(successToast)
      handleOpenChange(false)
    } catch {
      // runMutation already showed the error toast.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{fbt("What's wrong?", 'Title of the dialog for reporting a problem with a set')}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2" role="radiogroup">
          {categories.map(c => {
            const reported = pendingCategories.has(c)
            const selected = category === c
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={reported}
                onClick={() => setCategory(c)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-left text-sm cursor-pointer disabled:cursor-default',
                  selected ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground',
                  reported && 'opacity-60',
                )}
              >
                <span className="block font-medium text-foreground">{categoryLabel(c)}</span>
                {reported && (
                  <span className="flex items-center gap-1 text-xs">
                    <Check className="size-3" aria-hidden />
                    {fbt('Reported', 'Note on a set report category the user already reported and is pending')}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <Textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={notePlaceholder(category)}
          rows={3}
          maxLength={1000}
        />
        {withLink && (
          <Input
            type="url"
            inputMode="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder={fbt(
              'Link where you saw it (optional)',
              'Placeholder for the optional link on a report about an event listing',
            )}
            aria-invalid={!linkValid}
            maxLength={2000}
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {cancelButtonLabel()}
          </Button>
          <Button onClick={submit} disabled={!canSubmit || submitting}>
            {fbt('Send report', 'Button to submit a report about a problem with a set')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
